/**
 * PublishWizardModalComponent — presentational 3-step "Công bố thiết bị /
 * tính năng" wizard (EXT-04 / Phase 6 / Plan 06-05, MOCK-ONLY).
 *
 * Reuses the verified firmware-wizard chrome: <Modal> shell + <StepIndicator
 * totalSteps={3}> + @ramonak/react-progress-bar (navy #162A75, the verified
 * Phase-2 values). Three steps:
 *   step 0 — verify metadata (Mô tả textarea + Phiên bản text, default 1.0.0)
 *   step 1 — confirm package contents (MstemTable + confirm checkbox)
 *   step 2 — mock progress 0→100% → success + mock registry URL + copy link
 *
 * All user-facing strings flow through react-intl (gen.ext.publish.* +
 * gen.ext.common.close). The container drives step transitions, the progress
 * timer (leak-guarded), the mock URL generation and the clipboard copy.
 *
 * Implemented as a class (not an arrow function) so onChange/onClick handlers
 * bind via lodash.bindall — avoids react/jsx-no-bind from inline arrows.
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import ProgressBar from '@ramonak/react-progress-bar';
import {FormattedMessage, defineMessages, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import StepIndicator from '../firmware-wizard-modal/step-indicator.jsx';
import MstemTable from '../mstem-table/mstem-table.jsx';

import styles from './publish-wizard-modal.css';

const messages = defineMessages({
    title: {
        defaultMessage: 'Công bố thiết bị / tính năng',
        description: 'Title of the publish wizard (EXT-04)',
        id: 'gen.ext.publish.title'
    },
    step1: {
        defaultMessage: 'Xác minh',
        description: 'Publish wizard step 1 label',
        id: 'gen.ext.publish.step1'
    },
    step2: {
        defaultMessage: 'Xác nhận',
        description: 'Publish wizard step 2 label',
        id: 'gen.ext.publish.step2'
    },
    step3: {
        defaultMessage: 'Công bố',
        description: 'Publish wizard step 3 label',
        id: 'gen.ext.publish.step3'
    },
    verifyTitle: {
        defaultMessage: 'Xác minh thông tin',
        description: 'Publish wizard step 1 heading',
        id: 'gen.ext.publish.verifyTitle'
    },
    descLabel: {
        defaultMessage: 'Mô tả',
        description: 'Publish wizard description field label',
        id: 'gen.ext.publish.descLabel'
    },
    versionLabel: {
        defaultMessage: 'Phiên bản',
        description: 'Publish wizard version field label',
        id: 'gen.ext.publish.versionLabel'
    },
    confirmTitle: {
        defaultMessage: 'Xác nhận nội dung gói',
        description: 'Publish wizard step 2 heading',
        id: 'gen.ext.publish.confirmTitle'
    },
    confirmCheck: {
        defaultMessage: 'Tôi xác nhận nội dung gói công bố là chính xác.',
        description: 'Publish wizard confirm checkbox label',
        id: 'gen.ext.publish.confirmCheck'
    },
    progress: {
        defaultMessage: 'Đang công bố… {percent}%',
        description: 'Publish wizard in-progress label',
        id: 'gen.ext.publish.progress'
    },
    successTitle: {
        defaultMessage: 'Công bố thành công!',
        description: 'Publish wizard success heading',
        id: 'gen.ext.publish.successTitle'
    },
    urlLabel: {
        defaultMessage: 'Đường dẫn registry (mô phỏng):',
        description: 'Publish wizard mock registry URL label',
        id: 'gen.ext.publish.urlLabel'
    },
    copyLink: {
        defaultMessage: 'Sao chép link',
        description: 'Publish wizard copy URL button',
        id: 'gen.ext.publish.copyLink'
    },
    next: {
        defaultMessage: 'Tiếp theo',
        description: 'Publish wizard next button',
        id: 'gen.ext.publish.next'
    },
    publish: {
        defaultMessage: 'Công bố',
        description: 'Publish wizard publish button',
        id: 'gen.ext.publish.publish'
    },
    errVersion: {
        defaultMessage: 'Vui lòng nhập số phiên bản.',
        description: 'Publish wizard empty-version validation error',
        id: 'gen.ext.publish.errVersion'
    },
    errConfirm: {
        defaultMessage: 'Hãy tích xác nhận trước khi công bố.',
        description: 'Publish wizard unconfirmed validation error',
        id: 'gen.ext.publish.errConfirm'
    },
    cancel: {
        defaultMessage: 'Hủy',
        description: 'EXT-04 publish wizard cancel button',
        id: 'gen.ext.publish.cancel'
    },
    close: {
        defaultMessage: 'Đóng',
        description: 'Publish wizard close button (shared)',
        id: 'gen.ext.common.close'
    },
    pkgName: {
        defaultMessage: 'Tệp',
        description: 'Publish package table — file name column',
        id: 'gen.ext.publish.colName'
    },
    pkgSize: {
        defaultMessage: 'Dung lượng',
        description: 'Publish package table — size column',
        id: 'gen.ext.publish.colSize'
    }
});

class PublishWizardModalComponent extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleDescChange',
            'handleVersionChange',
            'handleToggleConfirm',
            'renderSizeCell'
        ]);
    }
    handleDescChange (e) {
        this.props.onDescChange(e.target.value);
    }
    handleVersionChange (e) {
        this.props.onVersionChange(e.target.value);
    }
    handleToggleConfirm (e) {
        this.props.onToggleConfirm(e.target.checked);
    }
    renderSizeCell (row) {
        return `${row.sizeKB} KB`;
    }
    render () {
        const {
            intl,
            step,
            description,
            version,
            confirmed,
            progress,
            isComplete,
            publishUrl,
            packageItems,
            onNext,
            onCopyLink,
            onRequestClose
        } = this.props;

        const stepLabels = [
            intl.formatMessage(messages.step1),
            intl.formatMessage(messages.step2),
            intl.formatMessage(messages.step3)
        ];
        const versionMissing = version.trim().length === 0;
        const columns = [
            {key: 'name', label: intl.formatMessage(messages.pkgName)},
            {key: 'sizeKB', label: intl.formatMessage(messages.pkgSize), render: this.renderSizeCell}
        ];

        return (
            <Modal
                className={styles.modalContent}
                contentLabel={intl.formatMessage(messages.title)}
                id="publishWizardModal"
                onRequestClose={onRequestClose}
            >
                <div className={styles.body}>
                    <StepIndicator
                        currentStep={step}
                        labels={stepLabels}
                        totalSteps={3}
                    />
                    <div className={styles.stepContent}>
                        {step === 0 ? (
                            <div>
                                <h3 className={styles.stepHeading}>
                                    <FormattedMessage {...messages.verifyTitle} />
                                </h3>
                                <label className={styles.fieldLabel}>
                                    <FormattedMessage {...messages.descLabel} />
                                </label>
                                <textarea
                                    className={styles.textarea}
                                    rows={3}
                                    value={description}
                                    onChange={this.handleDescChange}
                                />
                                <label className={styles.fieldLabel}>
                                    <FormattedMessage {...messages.versionLabel} />
                                </label>
                                <input
                                    className={styles.input}
                                    type="text"
                                    value={version}
                                    onChange={this.handleVersionChange}
                                />
                                {versionMissing ? (
                                    <p className={styles.error}>
                                        <FormattedMessage {...messages.errVersion} />
                                    </p>
                                ) : null}
                            </div>
                        ) : null}
                        {step === 1 ? (
                            <div>
                                <h3 className={styles.stepHeading}>
                                    <FormattedMessage {...messages.confirmTitle} />
                                </h3>
                                <MstemTable
                                    columns={columns}
                                    data={packageItems}
                                />
                                <label className={styles.checkRow}>
                                    <input
                                        type="checkbox"
                                        checked={confirmed}
                                        onChange={this.handleToggleConfirm}
                                    />
                                    <span className={styles.checkLabel}>
                                        <FormattedMessage {...messages.confirmCheck} />
                                    </span>
                                </label>
                                {confirmed ? null : (
                                    <p className={styles.error}>
                                        <FormattedMessage {...messages.errConfirm} />
                                    </p>
                                )}
                            </div>
                        ) : null}
                        {step === 2 ? (
                            <div className={styles.progressWrap}>
                                {isComplete ? (
                                    <div className={styles.successBox}>
                                        <span
                                            className={styles.successCheck}
                                            aria-hidden="true"
                                        >
                                            {'✓'}
                                        </span>
                                        <h3 className={styles.successTitle}>
                                            <FormattedMessage {...messages.successTitle} />
                                        </h3>
                                        <p className={styles.urlLabel}>
                                            <FormattedMessage {...messages.urlLabel} />
                                        </p>
                                        <input
                                            readOnly
                                            className={styles.urlInput}
                                            type="text"
                                            value={publishUrl}
                                        />
                                        <button
                                            className={styles.btnPrimary}
                                            type="button"
                                            onClick={onCopyLink}
                                        >
                                            <FormattedMessage {...messages.copyLink} />
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <p className={styles.progressLabel}>
                                            <FormattedMessage
                                                {...messages.progress}
                                                values={{percent: Math.round(progress)}}
                                            />
                                        </p>
                                        <ProgressBar
                                            baseBgColor="#D9E3F2"
                                            bgColor="#162A75"
                                            completed={Math.round(progress)}
                                            height="8px"
                                            isLabelVisible={false}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                    <div className={styles.footer}>
                        {step === 2 && isComplete ? null : (
                            <button
                                className={styles.btnSecondary}
                                type="button"
                                onClick={onRequestClose}
                            >
                                <FormattedMessage {...messages.cancel} />
                            </button>
                        )}
                        {step === 0 ? (
                            <button
                                className={styles.btnPrimary}
                                disabled={versionMissing}
                                type="button"
                                onClick={onNext}
                            >
                                <FormattedMessage {...messages.next} />
                            </button>
                        ) : null}
                        {step === 1 ? (
                            <button
                                className={styles.btnPrimary}
                                disabled={!confirmed}
                                type="button"
                                onClick={onNext}
                            >
                                <FormattedMessage {...messages.publish} />
                            </button>
                        ) : null}
                        {step === 2 && isComplete ? (
                            <button
                                className={styles.btnPrimary}
                                type="button"
                                onClick={onRequestClose}
                            >
                                <FormattedMessage {...messages.close} />
                            </button>
                        ) : null}
                    </div>
                </div>
            </Modal>
        );
    }
}

PublishWizardModalComponent.propTypes = {
    confirmed: PropTypes.bool.isRequired,
    description: PropTypes.string.isRequired,
    intl: intlShape.isRequired,
    isComplete: PropTypes.bool.isRequired,
    onCopyLink: PropTypes.func.isRequired,
    onDescChange: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onToggleConfirm: PropTypes.func.isRequired,
    onVersionChange: PropTypes.func.isRequired,
    packageItems: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        sizeKB: PropTypes.number
    })).isRequired,
    progress: PropTypes.number.isRequired,
    publishUrl: PropTypes.string.isRequired,
    step: PropTypes.number.isRequired,
    version: PropTypes.string.isRequired
};

export default injectIntl(PublishWizardModalComponent);
